/**
 * Resolves quote-reply anchors (quoted text + occurrence) back to DOM
 * Ranges over the RENDERED script, and paints them via the CSS Custom
 * Highlight API — no DOM mutation, spans element boundaries (bold /
 * italic / links inside a quote are fine).
 *
 * Everything degrades silently: unsupported browser → no inline
 * highlight (the comment is still visible in chat + Line-by-Line);
 * quote no longer present (script replaced) → that anchor is skipped.
 */

/** Anchor subset the highlighter needs. */
export type HighlightableAnchor = {
  quoted_text: string;
  occurrence?: number;
  /** The chat message this anchor came from — carried through purely for
   *  the "View comment" hover affordance (jump from the highlighted script
   *  line to its message in chat). Not used for matching/painting itself. */
  messageId?: string;
};

/** One anchor successfully resolved to a live Range in the rendered script —
 *  what applyAnchorHighlights' optional onResolved callback receives. */
export type ResolvedAnchorHighlight = { anchor: HighlightableAnchor; range: Range };

export const SCRIPT_HIGHLIGHT_NAME = 'script-open-comments';
/**
 * Open comments each register under their OWN layer name (round-robin
 * across this many), instead of one shared Highlight under
 * SCRIPT_HIGHLIGHT_NAME — the Custom Highlight API paints all the ranges
 * within a single Highlight as a union (a span covered by two overlapping
 * ranges in the SAME Highlight looks identical to one covered by just one),
 * so text under two or three open comments looked no different from text
 * under a single one. Independent ::highlight() layers painting the same
 * low-alpha purple over each other DO compound via ordinary alpha
 * compositing, so a passage under more open comments reads visibly darker —
 * matching Google Docs. 8 simultaneous open comments on overlapping text is
 * already a lot for a real script review; beyond that, anchors wrap around
 * and share a layer (still painted, just no longer distinctly stacked).
 */
export const SCRIPT_HIGHLIGHT_OPEN_LAYERS = 8;
export function scriptOpenHighlightLayerName(index: number): string {
  return `${SCRIPT_HIGHLIGHT_NAME}-${index}`;
}
/** One-shot "you just landed here" flash — distinct from the persistent
 *  open/resolved-comment highlights above, and always cleared after a
 *  few seconds rather than staying registered. */
export const SCRIPT_HIGHLIGHT_FOCUS_NAME = 'script-jump-focus';

// The Highlight API is newer than our TS DOM lib — feature-detect at
// runtime and keep the types local.
type HighlightRegistry = Map<string, unknown> & {
  set(name: string, highlight: unknown): void;
  delete(name: string): boolean;
};
type HighlightCtor = new (...ranges: Range[]) => unknown;

function registry(): HighlightRegistry | null {
  const css = globalThis.CSS as (typeof CSS & { highlights?: HighlightRegistry }) | undefined;
  return css?.highlights ?? null;
}

function highlightCtor(): HighlightCtor | null {
  return (globalThis as { Highlight?: HighlightCtor }).Highlight ?? null;
}

/**
 * Find the `occurrence`-th (0-based) match of `quote` in `container`'s
 * rendered text and return it as a Range, or null when it doesn't exist.
 * Matches may span multiple text nodes.
 */
export function findAnchorRange(
  container: Node,
  quote: string,
  occurrence = 0,
): Range | null {
  if (!quote) return null;

  const doc = container.ownerDocument ?? (container as Document);
  const walker = doc.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  const starts: number[] = []; // cumulative char offset of each node's start
  let full = '';
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    nodes.push(n as Text);
    starts.push(full.length);
    full += (n as Text).data;
  }

  // Nth non-overlapping match in the concatenated text.
  let idx = -1;
  for (let i = 0; i <= occurrence; i += 1) {
    idx = full.indexOf(quote, idx + 1 === 0 ? 0 : idx + quote.length);
    if (idx === -1) return null;
  }
  const end = idx + quote.length;

  // Map a global char offset back to (text node, local offset).
  const locate = (offset: number, isEnd: boolean): { node: Text; local: number } | null => {
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
      const start = starts[i]!;
      const len = nodes[i]!.data.length;
      // End offsets may sit exactly at a node boundary (start + len).
      if (offset > start || (offset === start && (!isEnd || len === 0))) {
        if (offset <= start + len) return { node: nodes[i]!, local: offset - start };
      }
    }
    return null;
  };

  const startLoc = locate(idx, false);
  const endLoc = locate(end, true);
  if (!startLoc || !endLoc) return null;

  const range = doc.createRange();
  range.setStart(startLoc.node, startLoc.local);
  range.setEnd(endLoc.node, endLoc.local);
  return range;
}

/**
 * Assigns each range in `ranges` a layer index in [0, layerCount) via
 * interval graph coloring (a standard sweep-line: process ranges in
 * document order, reuse the first layer whose current occupant has already
 * ENDED, otherwise open a new one) — two ranges only ever share a layer when
 * they do NOT overlap. This is what actually matters: the CSS Custom
 * Highlight API's alpha compositing only visibly compounds where ranges
 * physically overlap the same characters, so non-overlapping ranges sharing
 * a layer is free (no visual difference), while overlapping ranges sharing
 * a layer WOULD silently flatten (a Highlight's own ranges paint as a
 * union — see SCRIPT_HIGHLIGHT_OPEN_LAYERS' doc comment). The true bound on
 * layers needed is therefore the maximum overlap DEPTH (how many ranges
 * cover the same character at once), not the total range count — a plain
 * round-robin (`i % layerCount`) has no such guarantee: two overlapping
 * ranges exactly `layerCount` apart in insertion order would collide onto
 * the same layer regardless of whether they overlap.
 *
 * `ranges` must already be in document order (start-position ascending) —
 * the sweep only frees a layer by comparing against ranges processed so
 * far, so an out-of-order input can't reliably detect "already ended".
 *
 * Falls back to `index % layerCount` only once genuine overlap depth
 * exceeds the cap — the same degrade-gracefully behavior the old code had,
 * just triggered by overlap depth instead of raw count.
 */
export function assignHighlightLayers(ranges: Range[], layerCount: number): number[] {
  // activeEnds[layer] = the Range currently occupying that layer, or null if free.
  const activeEnds: (Range | null)[] = new Array(layerCount).fill(null);
  return ranges.map((range, index) => {
    for (let layer = 0; layer < layerCount; layer += 1) {
      const occupant = activeEnds[layer];
      // range.compareBoundaryPoints(END_TO_START, occupant) compares
      // OCCUPANT's end position to RANGE's start position — but Range's
      // compareBoundaryPoints return convention is the OPPOSITE of the
      // usual comparator sign (verified empirically against jsdom: a
      // strictly EARLIER boundary point returns +1, a strictly LATER one
      // returns -1, 0 only on an exact position match), so ">= 0" here
      // means "occupant's end is at-or-before range's start" — i.e. the
      // occupant has already ended and this layer is free to reuse.
      if (occupant && range.compareBoundaryPoints(Range.END_TO_START, occupant) >= 0) {
        activeEnds[layer] = null;
      }
    }
    const freeLayer = activeEnds.findIndex((occupant) => occupant === null);
    if (freeLayer === -1) return index % layerCount;
    activeEnds[freeLayer] = range;
    return freeLayer;
  });
}

/**
 * Paint every resolvable anchor inside `container` as an open comment.
 * Re-invocable — each call replaces the previous registration. Returns how
 * many anchors resolved to a live range (0 also when the API is unsupported).
 *
 * `onResolved`, when given, receives the (anchor, range) pairs that actually
 * matched — the same matching pass this function already does internally,
 * exposed rather than duplicated, so a caller (the "View comment" hover
 * affordance) can hit-test the pointer against these exact ranges without
 * re-walking the DOM itself.
 */
export function applyAnchorHighlights(
  container: Element,
  anchors: HighlightableAnchor[],
  onResolved?: (resolved: ResolvedAnchorHighlight[]) => void,
): number {
  const reg = registry();
  const Ctor = highlightCtor();
  if (!reg || !Ctor) {
    onResolved?.([]);
    return 0;
  }

  const resolved = anchors
    .map((a) => ({ anchor: a, range: findAnchorRange(container, a.quoted_text, a.occurrence ?? 0) }))
    .filter((p): p is ResolvedAnchorHighlight => p.range !== null);
  onResolved?.(resolved);

  const openRanges = resolved.map((p) => p.range);

  for (let i = 0; i < SCRIPT_HIGHLIGHT_OPEN_LAYERS; i += 1) {
    reg.delete(scriptOpenHighlightLayerName(i));
  }
  // Document order, not anchor-array order — assignHighlightLayers' sweep
  // needs ranges in start-position order to correctly detect "this layer's
  // occupant has already ended, reuse it".
  const orderedOpenRanges = [...openRanges].sort((a, b) =>
    a.compareBoundaryPoints(Range.START_TO_START, b),
  );
  const layers = assignHighlightLayers(orderedOpenRanges, SCRIPT_HIGHLIGHT_OPEN_LAYERS);
  orderedOpenRanges.forEach((range, i) => {
    const name = scriptOpenHighlightLayerName(layers[i]!);
    const existing = reg.get(name) as { add?: (r: Range) => void } | undefined;
    if (typeof existing?.add === 'function') existing.add(range);
    else reg.set(name, new Ctor(range));
  });

  return openRanges.length;
}

/**
 * Remove the PERSISTENT script open-comment highlight registrations — this
 * is the cleanup for the effect that re-applies them whenever anchors/
 * bodyText change, which can fire independently of, and more often than,
 * the one-shot focus flash. It deliberately does NOT touch
 * SCRIPT_HIGHLIGHT_FOCUS_NAME: that highlight owns its own lifecycle via
 * applyFocusHighlight/clearFocusHighlight (see the flash effect in
 * ScriptResultTabs). Clearing it here too — as an earlier version did —
 * meant every re-render of this persistent effect (e.g. from the several
 * setState calls a chat-anchor jump click triggers) tore the in-progress
 * amber flash back out mid-animation, leaving only the persistent purple
 * highlight visible and reading as the flash never happened / "went dark".
 */
export function clearAnchorHighlights(): void {
  const reg = registry();
  for (let i = 0; i < SCRIPT_HIGHLIGHT_OPEN_LAYERS; i += 1) {
    reg?.delete(scriptOpenHighlightLayerName(i));
  }
}

/**
 * Flash `quote` (occurrence-th match) as the one-shot "you just landed
 * here" highlight — e.g. after scrolling to a line from a chat anchor
 * click or an AI row's Comment click. Caller owns clearing it (typically
 * a short timeout via clearFocusHighlight); unlike applyAnchorHighlights
 * this doesn't get re-invoked on every render, so nothing else does it
 * for you. Returns whether a match was found and highlighted.
 */
export function applyFocusHighlight(
  container: Element,
  quote: string,
  occurrence = 0,
): boolean {
  const reg = registry();
  const Ctor = highlightCtor();
  if (!reg || !Ctor) return false;
  const range = findAnchorRange(container, quote, occurrence);
  if (!range) {
    reg.delete(SCRIPT_HIGHLIGHT_FOCUS_NAME);
    return false;
  }
  reg.set(SCRIPT_HIGHLIGHT_FOCUS_NAME, new Ctor(range));
  return true;
}

/** Clears just the one-shot focus flash, leaving open/resolved highlights be. */
export function clearFocusHighlight(): void {
  registry()?.delete(SCRIPT_HIGHLIGHT_FOCUS_NAME);
}
